import { Icon, Text } from "@assetwatch/ui";
import type { FeedbackVerdict } from "./api";
import { FeatureVerdictBoxes } from "./FeatureVerdictBoxes";
import {
  chartFeatureStems,
  type FeatureVerdicts,
  featureDescription,
  featureLabel,
} from "./lib";
import { useCmeFeatureDescriptions } from "./queries";
import type { RailItem } from "./types";

/**
 * Middle box of the feedback pane: every detected feature on the machine's
 * monitoring points, one clickable card each with its Useful / Not useful
 * boxes. The selected card is graphed below, and its description fills the
 * rest of the box. Descriptions come from a file bundled with the API, so
 * loading them up front is free.
 */
export function FeatureList({
  points,
  verdicts,
  selected,
  locked,
  onSelect,
  onVerdict,
}: {
  points: RailItem[];
  verdicts: FeatureVerdicts;
  selected: { pointId: string; featureId: string };
  /** Saved: ratings show greyed out until Edit. Rows still pick the graph. */
  locked: boolean;
  onSelect: (pointId: string, featureId: string) => void;
  onVerdict: (
    pointId: string,
    featureId: string,
    verdict: FeedbackVerdict | null,
  ) => void;
}) {
  const descriptions = useCmeFeatureDescriptions(true);
  const rows = points.flatMap((point) =>
    chartFeatureStems(point).map((stem) => ({ point, stem })),
  );
  const current = rows.find(
    ({ point, stem }) =>
      point.id === selected.pointId && stem.id === selected.featureId,
  );

  return (
    <div className="flex flex-1 flex-col gap-(--spacing-8)">
      <Text size="sm" tone="subtle">
        Select a feature to view its graph and description.
      </Text>
      <ul className="m-0 flex list-none flex-col gap-(--spacing-6) p-0">
        {rows.map(({ point, stem }) => {
          const isSelected = current?.point === point && current.stem === stem;
          const label = `${point.monitoring_point || "Unknown point"} · ${featureLabel(stem.stem)}`;
          return (
            <li
              className={`flex items-center gap-(--spacing-12) rounded-(--radius-sm) border px-(--spacing-12) py-(--spacing-6) transition-colors ${isSelected ? "border-primary bg-surface-selected" : "border-outline-subtle bg-surface hover:border-outline hover:bg-surface-hover"}`}
              key={`${point.id}:${stem.id}`}
            >
              {/* The label is the click target; the checkboxes stay separate controls. */}
              <button
                aria-current={isSelected ? "true" : undefined}
                className="flex min-w-0 flex-1 cursor-pointer items-center gap-(--spacing-8) rounded-(--radius-xs) border-0 bg-transparent p-0 text-left outline-none focus-visible:ring-2 focus-visible:ring-button-focus-ring"
                onClick={() => onSelect(point.id, stem.id)}
                type="button"
              >
                <Icon
                  name="chart-line"
                  size="sm"
                  tone={isSelected ? "brand" : "neutral"}
                />
                <Text truncate weight={isSelected ? "bold" : "medium"}>
                  {label}
                </Text>
              </button>
              <FeatureVerdictBoxes
                disabled={locked}
                featureLabel={label}
                onVerdict={(next) => onVerdict(point.id, stem.id, next)}
                verdict={verdicts[point.id]?.[stem.id] ?? null}
              />
            </li>
          );
        })}
      </ul>
      {current ? (
        <div className="flex-1 rounded-(--radius-sm) border border-outline-subtle bg-surface px-(--spacing-12) py-(--spacing-8)">
          <Text size="sm" weight="bold">
            {current.stem.feature_group} · {featureLabel(current.stem.stem)}
          </Text>
          <div className="mt-(--spacing-4) whitespace-pre-line">
            <Text size="sm" tone="subtle">
              {descriptions.isPending
                ? "Loading description…"
                : descriptions.isError
                  ? "The description could not be loaded."
                  : (featureDescription(descriptions.data, current.stem.stem) ??
                    `No description yet for ${featureLabel(current.stem.stem)}.`)}
            </Text>
          </div>
        </div>
      ) : null}
    </div>
  );
}
